import React, { useEffect, useState } from 'react'
import { usePageContext } from '../../components/pages/pageContext'
import { useAppContext } from '../../context/appContext';
import { ImageContainer } from './modules/imageContainer';
import { SmoothScroll } from '../../components/smoothScroll';
import downArrowIcon from '../../assets/icons/arrow_down.svg';
import { Text2xl, Text3xl, TextLg, TextMd, TextSm, TextXl } from '../../components/text';
import { AboutMe } from './modules/aboutMe';
import { WorkHistory } from './modules/workHistory';
import { DataAnalyticsSection } from './modules/dataAnalytics';
import { WebDevelopmentSection } from './modules/webDevelopment';
import { ProcessAutomationSection } from './modules/processAutomation';
import { FadeInSection } from '../../components/fadeInSection/fadeInSection';

const messages = [
  {
    title: "Data Analytics",
    description:
      "Interactive dashboards, reporting automation, and actionable insights built from your datasets."
  },
  {
    title: "Process Automation",
    description:
      "Process automation, custom dashboards, and data solutions designed to help businesses work smarter with reduced workload, and grow faster."
  },
  {
    title: "Web Development",
    description:
      "Fast, modern websites and web applications crafted with performance, usability, and scalability in mind."
  }
];

export const Home = () => {
  const { routing, setPageLoading, setLoading } = useAppContext();
  const { navigate, baseUrl } = routing;
  const [active, setActive] = useState(0);
  
  useEffect(()=>{
    setPageLoading(true);
  },[]);

  return (
    <div className='bg-blue-50'>
      <ImageContainer
        active={active}
        setActive={setActive}
        onLoad={()=>{
          setPageLoading(false);
        }}
        onChange={(index)=>{
          setActive(index);
        }}
        className={`
          min-h-[60vh] lg:h-[100vh] w-[100vw]
          z-[10] relative bg-gradient
          [&_.dotContainer]:justify-center lg:[&_.dotContainer]:justify-start
          [&_.dot]:h-3 [&_.dot]:w-3
          lg:[&_.dot]:h-3.5 lg:[&_.dot]:w-3.5
        `}
      >
        <div 
          className='absolute bottom-0 right-0 hidden lg:flex flex-col gap-2 p-6 z-[6] text-white/80 hover:text-white transition cursor-pointer select-none'
          onClick={()=>{
            window.scrollTo(0, window.innerHeight);
          }}
        >
          <TextSm>Scroll Down</TextSm>
          <img className='white-svg bounce h-6' src={downArrowIcon} />
        </div>

        <div className='absolute inset-0 pointer-events-none flex justify-center items-center text-gray-100 z-[6]'>
          <div 
            className={`
              flex w-3/5 lg:w-2/5 h-2/5 z-[6] cursor-pointer rounded-xl
              pointer-events-auto select-none relative
            `}
            onClick={()=> setActive((prev)=> (prev + 1) % 3)}
          >
            <div 
              className='shadow-lg absolute left-1/3 top-1/2 rounded-full w-0 h-0'
              style={{ boxShadow: 'rgba(0, 0, 0, 0.25) 1px 1px 298px 131px' }} 
            />
            <div 
              className='shadow-lg absolute left-1/2 top-1/2 rounded-full w-0 h-0'
              style={{ boxShadow: 'rgba(0, 0, 0, 0.25) 1px 1px 298px 131px' }} 
            />
            <div 
              className='shadow-lg absolute left-2/3 top-1/2 rounded-full w-0 h-0'
              style={{ boxShadow: 'rgba(0, 0, 0, 0.25) 1px 1px 298px 131px' }} 
            />

            {
              messages.map(({ title, description }, i)=>(
                <div 
                  className={`absolute inset-0 flex flex-col justify-center items-center gap-6 transition-opacity duration-[750ms] ease-in-out text-center`}
                  key={`home_section_${title}`}
                  style={{
                    opacity: active === i ? 100 : 0,
                    transitionDelay: active === i ? 1500 : 0,
                  }}
                >
                  <Text2xl className='font-bold'>{ title }</Text2xl>
                  <TextMd className='font-medium'>
                    { description }
                  </TextMd>
                </div>
              ))
            }
          </div>
          
        </div>

      </ImageContainer>

      <div 
        className="min-h-screen flex flex-col text-black z-[1]"
      >
        <FadeInSection left threshold={0.25} className='m-1'>
          <DataAnalyticsSection className='p-9' />
        </FadeInSection>
        
        <div className='h-px mx-9 bg-neutral-500/15' />
            
        {/* <ProcessAutomationSection className='p-9' />
        <div className='h-px mx-9 bg-neutral-500/15' /> */}
        <FadeInSection left threshold={0.25} className='m-1'>
          <WebDevelopmentSection className='p-9' />
        </FadeInSection>
        
        <div className='h-px mx-9 bg-neutral-500/15' />
          
        <button 
          className={`
            py-12 w-full relative overflow-hidden !rounded-[0px]
          `}
          onClick={() => navigate('/contact')}
        >
          <div className='absolute inset-0 z-[1]'>
            <img 
              className={`hidden 2xl:flex bg-fixed bg-cover absolute inset-0 h-screen w-screen`}
              style={{
                backgroundPosition: 'center center',
                backgroundImage: `url("${baseUrl}/images/table.png")`,
              }}
            />
          
            <img
              className='flex 2xl:hidden h-full w-full bg-cover'
              style={{
                backgroundPosition: 'center center',
                backgroundImage: `url("${baseUrl}/images/table.png")`,
              }}
            />

            <div 
              className='absolute inset-0 backdrop-blur-sm'
              style={{
                boxShadow: "0rem 300rem 0rem 0rem rgba(0,0,0,.4) inset",
              }}
            />
          </div>
          
          <div 
            className='relative flex flex-col h-full z-[5] items-center justify-center px-9 select-none'
          >
            <FadeInSection threshold={0.25}>
              <TextLg className='text-blue-600 font-bold'>Contact</TextLg>

              <TextMd className='text-gray-300 max-w-[60vw] text-center mt-3 font-semibold'>
                Have a project, workflow, or idea you want to improve? Let’s talk about
                how data analytics, automation, or web development can help your business
                work smarter.
              </TextMd>

              <TextMd
                className='get-in-touch mt-8 px-6 py-3 text-gray-300 font-semibold hover:text-white transition'
              >
                Get in Touch
              </TextMd>
            </FadeInSection>
          </div>
        </button>

        <AboutMe className='p-9'/>
          
        <WorkHistory className={`p-9`} />
      </div>
    </div>
  )
}
